import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { SerializedError } from '@reduxjs/toolkit';
import { AppDispatch } from 'renderer/shared/state/store';
import { AuthResponse } from 'renderer/features/auth/schemas/auth.schemas';
import { setCredentials } from 'renderer/features/auth/state/userSlice';
import { setTokens } from 'renderer/features/auth/state/authSlice';
import { PurchaseResponse } from '../schemas/purchase.schemas';

type CheckoutAuthPayload = Pick<AuthResponse, 'user' | 'tokens'>;

export const hasCheckoutAuth = (
  payload: CheckoutAuthPayload | null | undefined,
): payload is Required<CheckoutAuthPayload> =>
  Boolean(payload?.user && payload?.tokens);

export const isGuestCreateAccountCheckout = (
  user?: { create?: boolean; id?: string } | null,
): boolean => Boolean(user?.create && !user?.id);

export const wantsCheckoutAuth = (requestBody: unknown): boolean => {
  if (!requestBody || typeof requestBody !== 'object') return false;

  if ('user' in requestBody) {
    const { user } = requestBody as {
      user?: { create?: boolean; id?: string };
    };
    return isGuestCreateAccountCheckout(user);
  }

  if ('createAccount' in requestBody) {
    return Boolean((requestBody as { createAccount?: boolean }).createAccount);
  }

  return false;
};

export const isPurchaseConfirmation = (
  payload:
    | (CheckoutAuthPayload & {
        transactionId?: string;
        transactions?: unknown[];
        success?: boolean;
        type?: string;
      })
    | null
    | undefined,
): payload is PurchaseResponse => {
  if (!payload || typeof payload !== 'object') return false;
  if (payload.type === 'requires_action') return false;
  if (payload.success === false) return false;

  return Boolean(
    payload.transactionId || (payload.transactions?.length ?? 0) > 0,
  );
};

const sanitizeCheckoutUser = (
  user: NonNullable<CheckoutAuthPayload['user']>,
): NonNullable<CheckoutAuthPayload['user']> => {
  const safeUser = {
    ...(user as NonNullable<CheckoutAuthPayload['user']> & {
      password?: string;
    }),
  };
  delete safeUser.password;
  return safeUser;
};

export const applyCheckoutAuth = (
  dispatch: AppDispatch,
  payload: CheckoutAuthPayload | null | undefined,
) => {
  if (!hasCheckoutAuth(payload)) return;

  const user = sanitizeCheckoutUser(payload.user);
  const authPayload = { user, tokens: payload.tokens };

  dispatch(setCredentials(authPayload));
  dispatch(setTokens(authPayload));
};

const readCheckoutAuthFromRtkError = (
  error: unknown,
): CheckoutAuthPayload | null | undefined => {
  if (!error || typeof error !== 'object') return undefined;

  if ('data' in error) {
    return (error as { data?: CheckoutAuthPayload }).data;
  }

  if ('error' in error) {
    const nested = (error as { error?: FetchBaseQueryError }).error;
    if (nested && typeof nested === 'object' && 'data' in nested) {
      return nested.data as CheckoutAuthPayload;
    }
  }

  return undefined;
};

export const applyCheckoutAuthFromError = (
  dispatch: AppDispatch,
  error: FetchBaseQueryError | SerializedError | unknown,
  options?: { allowAuth?: boolean },
) => {
  if (options?.allowAuth === false) return;

  const authPayload = readCheckoutAuthFromRtkError(error);
  applyCheckoutAuth(dispatch, authPayload);
};
